import { Injectable } from '@angular/core';
import { APP_CONFIG } from '../../environment';
import { ICalculateOrderResponseEntity } from '../../model/api/price-calculator/calculate-order-response.entity.interface';
import { IPriceCalculator } from '../../model/models/price-calculator/price-calculator.interface';
import { IOrder } from '../../model/models/order/order-interface';
import { HttpProvider } from '../http/http';
import { PriceCalculatorMapper } from '../price-calculator/price-calculator.mapper';
import { NumberFormatter } from '../../pipes/currency-pipe';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_AMOUNTS } from '../myria/app-variables';
import { ResourceProvider } from '../../providers/resource/resource';
import { UtilsProvider } from '../myria/utils-provider';
import { OrderMapper } from '../order/order.mapper';

@Injectable()
export class PriceCalculatorProvider {
  changeCurrency: boolean = false;
  lastCalculateResult = null;
  modalErrorActive: boolean = false;
  
  constructor(private httpProvider: HttpProvider,
              private currencyFormatter: NumberFormatter,
              private translate: TranslateService) {
  }
  /**
* @description Calculate the amounts, get possible values (payment methods, etc.) for a transaction. This call is doing too much and is hard to name.
*/
 async calculateOrderTotals(resourceProvider: ResourceProvider, request: IOrder, state = null, populatePaymentMethods = true): Promise<IPriceCalculator> {
    let url = 'moneytransfercalculator/calculate';
    if(populatePaymentMethods && (!request.paymentMethods || (request.paymentMethods && request.paymentMethods.length === 0))) {
      request.paymentMethods = await resourceProvider.getPaymentMethods();
    }
    const apiRequest = PriceCalculatorMapper.fromOrderToCalculateRequest(request);

    // in reverse calculation we always have to send an amount from since otherwise it could affect the rate (i.e. India INR)
    // probably because the get exchange rate will return a different rate when the rate is 0 - trying to send the best possible amount from
    // we try to recalculate it with the last rate we have
    if (request.shouldCalcAmountFrom && !apiRequest.selections.amountFrom && request.amountTo &&
        this.lastCalculateResult && 
        this.lastCalculateResult.currencyFrom === request.currencyFrom && 
        this.lastCalculateResult.currencyTo === request.currencyTo &&
        this.lastCalculateResult.countryTo === request.countryTo) {
      apiRequest.selections.amountFrom = PriceCalculatorProvider.formatNumber(parseFloat(request.amountTo.toString()) / this.lastCalculateResult.exchangeRate, this.lastCalculateResult.decimalPrecision);
    }

    if (state) {
      apiRequest.selections = { ...apiRequest.selections, stateTo: state };
      url = 'moneytransfercalculator/calculate/v2';
    }
    return this.httpProvider.post(`${APP_CONFIG.remittanceApiBaseUrl}${url}`, apiRequest, (response: ICalculateOrderResponseEntity) => {
      const result = PriceCalculatorMapper.fromPriceCalculatorResponseEntity(this.translate, response, request.paymentMethods);
                  
      if (apiRequest.selections.shouldCalcAmountFrom) {
        result.amountFrom = PriceCalculatorProvider.formatNumber(result.amountFrom, result.decimalPrecision);
      }
      else {
        result.amountTo = PriceCalculatorProvider.formatNumber(result.amountTo, result.decimalPrecision);
      }
      this.lastCalculateResult = result;
      this.lastCalculateResult.currencyFrom = apiRequest.selections.currencyFrom;
      this.lastCalculateResult.currencyTo = apiRequest.selections.currencyTo;
      this.lastCalculateResult.countryTo = apiRequest.selections.countryTo;

      if (response.errorResponse && response.errorResponse.errors && response.errorResponse.errors.length > 0) {
        response['error'] = response.errorResponse;
        response['result'] = result;
        response = this.httpProvider.parseError(response);
        return null;
      }
      return result;
    });
  }

  static formatNumber(value, decimalPrecision: number) {
    if (value) {
      value = parseFloat(value.toFixed(decimalPrecision)).toString();
    }
    return value;
  }

  // We are rounding to 6 significant figures, but also counting leading zeroes
  static updateRate(initialRate) {
    if (initialRate) {
      let value;
      if (initialRate < 1) {
        const leadingZeroes = -Math.floor(Math.log10(initialRate + 1));
        value = initialRate.toPrecision((6 - 1 - leadingZeroes));
      }
      else if (initialRate.toFixed(0).length >= 6)
        value = initialRate.toFixed(0);
      else
        value = initialRate.toPrecision(6);
      return value;
    } 
    return null;
  }

  getRateAmount(amount, rate, currency) {
    const result = amount * rate;
    const formatted = this.currencyFormatter.transform(result, currency, true).replace(/,/g, '');
    return formatted;
  }

  // TODO IMPROVE !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
  async getActionSheetNavParams(order, orderProvider, deliveryMethods, paymentMethods, variableRates, navParams, variableFees) {
    return {
      deliveryMethods,
      paymentMethods,
      sendCurrency: order.currencyFrom,
      receiveCurrency: order.currencyTo,
      variableRates,
      listType: null,
      directBankBrand: await orderProvider.getDirectBankBrand(),
      stagedRiaBrand: await orderProvider.getStagedRiaBrand(),
      payNearMeBrand: orderProvider.getPayNearMeBrand(),
      sendAmount: order.amountFrom,
      variableFees,
      ...navParams
    };
  }

  getOrderHash(order, shouldCalcAmountFrom = order.shouldCalcAmountFrom) {
    const orderHash = {
      amountFrom: (!shouldCalcAmountFrom && order.amountFrom) ? order.amountFrom.toString() : null,
      amountTo: (shouldCalcAmountFrom && order.amountTo) ? order.amountTo.toString() : null,
      currencyFrom: order.currencyFrom,
      currencyTo: order.currencyTo,
      countryFrom: order.countryFrom,
      countryTo: order.countryTo,
      deliveryMethod: OrderMapper.effectiveDeliveryMethod(order),
      paymentMethod: order.paymentMethod,
    }
    return JSON.stringify(orderHash).toLowerCase();
  }

  /**
  * @description Returns minimum send amount for given country.
  * @param country Country code (i.e: CL)
  */
  getMinimumAmount (country: string): number {
    return DEFAULT_AMOUNTS[country] || DEFAULT_AMOUNTS.DEFAULT;
  }
}



// WEBPACK FOOTER //
// ./src/providers/price-calculator/price-calculator.ts